import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PublicHeader, PublicFooter } from "@/components/layouts/PublicLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2, CheckCircle, Building2, Cog, Brain, AlertTriangle, Rocket, Lock, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { getPrimaryIndustries, getSubCategories, getDetailCategories } from "@/data/industryHierarchy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AI EVALUATION QUESTIONNAIRE v1.1
 * 
 * Updated with:
 * - 3-tier industry classification (Primary, Sub-Category, Detail)
 * - Account creation gate after questionnaire completion
 * - Report generation progress indicator
 */

// Build questions dynamically since industry questions need special handling
const buildQuestions = (answers) => {
  const primaryIndustries = getPrimaryIndustries();
  const subCategories = answers.q1 ? getSubCategories(answers.q1) : [];
  const detailCategories = answers.q1 && answers.q1_sub ? getDetailCategories(answers.q1, answers.q1_sub) : [];

  return [
    // ═══════════════════════════════════════════════════════════════
    // SECTION A — BUSINESS IDENTITY & SCALE (6 Questions with 3-tier industry)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "q1",
      section: "A",
      sectionTitle: "Business Identity & Scale",
      sectionIcon: "Building2",
      question: "Primary Industry",
      type: "dropdown",
      options: primaryIndustries
    },
    {
      id: "q1_sub",
      section: "A",
      sectionTitle: "Business Identity & Scale",
      question: "Industry Sub-Category",
      type: "dropdown",
      dependsOn: "q1",
      options: subCategories,
      skipIf: !answers.q1
    },
    {
      id: "q1_detail",
      section: "A",
      sectionTitle: "Business Identity & Scale",
      question: "Industry Detail",
      type: "dropdown",
      dependsOn: "q1_sub",
      options: detailCategories,
      skipIf: !answers.q1_sub || detailCategories.length === 0
    },
    {
      id: "q2",
      section: "A",
      sectionTitle: "Business Identity & Scale",
      question: "Years in Operation",
      type: "radio",
      options: [
        { value: "3-5", label: "3–5 years" },
        { value: "6-10", label: "6–10 years" },
        { value: "11-20", label: "11–20 years" },
        { value: "20+", label: "20+ years" }
      ]
    },
    {
      id: "q3",
      section: "A",
      sectionTitle: "Business Identity & Scale",
      question: "Approximate Number of Employees",
      type: "radio",
      options: [
        { value: "1-10", label: "1–10" },
        { value: "11-25", label: "11–25" },
        { value: "26-50", label: "26–50" },
        { value: "51-100", label: "51–100" },
        { value: "100+", label: "100+" }
      ]
    },
    {
      id: "q4",
      section: "A",
      sectionTitle: "Business Identity & Scale",
      question: "Approximate Annual Revenue Range",
      type: "radio",
      options: [
        { value: "under_1m", label: "Under $1M" },
        { value: "1m_5m", label: "$1M–$5M" },
        { value: "5m_20m", label: "$5M–$20M" },
        { value: "20m+", label: "$20M+" },
        { value: "prefer_not_say", label: "Prefer not to say" }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // SECTION B — OPERATIONAL REALITY (5 Questions)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "q5",
      section: "B",
      sectionTitle: "Operational Reality",
      sectionIcon: "Cog",
      question: "How would you describe your day-to-day operations?",
      type: "radio",
      options: [
        { value: "mostly_manual", label: "Mostly manual and people-driven" },
        { value: "mix_manual_software", label: "Mix of manual processes and software" },
        { value: "mostly_digital", label: "Mostly digital with limited automation" },
        { value: "highly_systemized", label: "Highly systemized and process-driven" }
      ]
    },
    {
      id: "q6",
      section: "B",
      sectionTitle: "Operational Reality",
      question: "Which areas consume the most time or money?",
      subtitle: "Select up to 3",
      type: "multiselect",
      maxSelections: 3,
      options: [
        { value: "sales_marketing", label: "Sales & marketing" },
        { value: "operations_production", label: "Operations / production" },
        { value: "customer_service", label: "Customer service" },
        { value: "finance_accounting", label: "Finance & accounting" },
        { value: "staffing_hr", label: "Staffing & HR" },
        { value: "compliance_reporting", label: "Compliance & reporting" },
        { value: "it_systems", label: "IT / systems" }
      ]
    },
    {
      id: "q7",
      section: "B",
      sectionTitle: "Operational Reality",
      question: "How many core software tools are used daily?",
      type: "radio",
      options: [
        { value: "1-3", label: "1–3" },
        { value: "4-7", label: "4–7" },
        { value: "8-12", label: "8–12" },
        { value: "12+", label: "12+" }
      ]
    },
    {
      id: "q8",
      section: "B",
      sectionTitle: "Operational Reality",
      question: "How often do systems or processes break down or require manual fixes?",
      type: "radio",
      options: [
        { value: "rarely", label: "Rarely" },
        { value: "occasionally", label: "Occasionally" },
        { value: "frequently", label: "Frequently" },
        { value: "constantly", label: "Constantly" }
      ]
    },
    {
      id: "q9",
      section: "B",
      sectionTitle: "Operational Reality",
      question: "How confident are you in the accuracy of your operational data?",
      type: "radio",
      options: [
        { value: "very_confident", label: "Very confident" },
        { value: "somewhat_confident", label: "Somewhat confident" },
        { value: "not_very_confident", label: "Not very confident" },
        { value: "unsure", label: "Unsure" }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // SECTION C — A.I. AWARENESS & CURRENT EXPOSURE (5 Questions)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "q10",
      section: "C",
      sectionTitle: "A.I. Awareness & Current Exposure",
      sectionIcon: "Brain",
      question: "How familiar are you with A.I. in a business context?",
      type: "radio",
      options: [
        { value: "not_familiar", label: "Not familiar" },
        { value: "basic_awareness", label: "Basic awareness" },
        { value: "actively_exploring", label: "Actively exploring" },
        { value: "already_using", label: "Already using A.I. tools" }
      ]
    },
    {
      id: "q11",
      section: "C",
      sectionTitle: "A.I. Awareness & Current Exposure",
      question: "Are you currently using any A.I.-powered tools?",
      type: "radio",
      options: [
        { value: "no", label: "No" },
        { value: "yes_general", label: "Yes – general-purpose (e.g., chat tools)" },
        { value: "yes_industry", label: "Yes – industry-specific" },
        { value: "not_sure", label: "Not sure" }
      ]
    },
    {
      id: "q12",
      section: "C",
      sectionTitle: "A.I. Awareness & Current Exposure",
      question: "If yes or unsure, how were these tools introduced?",
      type: "radio",
      options: [
        { value: "personal_experimentation", label: "Personal experimentation" },
        { value: "staff_suggestion", label: "Staff suggestion" },
        { value: "vendor_recommendation", label: "Vendor recommendation" },
        { value: "consultant_recommendation", label: "Consultant recommendation" },
        { value: "not_applicable", label: "Not applicable" }
      ]
    },
    {
      id: "q13",
      section: "C",
      sectionTitle: "A.I. Awareness & Current Exposure",
      question: "What best describes your biggest concern about A.I.?",
      type: "radio",
      options: [
        { value: "choosing_wrong_tools", label: "Choosing the wrong tools" },
        { value: "wasting_money", label: "Wasting money" },
        { value: "data_security_privacy", label: "Data security / privacy" },
        { value: "staff_resistance", label: "Staff resistance" },
        { value: "falling_behind_competitors", label: "Falling behind competitors" },
        { value: "not_concerned", label: "Not concerned" }
      ]
    },
    {
      id: "q14",
      section: "C",
      sectionTitle: "A.I. Awareness & Current Exposure",
      question: "How confident are you in explaining A.I. decisions to stakeholders?",
      type: "radio",
      options: [
        { value: "very_confident", label: "Very confident" },
        { value: "somewhat_confident", label: "Somewhat confident" },
        { value: "not_confident", label: "Not confident" },
        { value: "have_not_considered", label: "Have not considered this" }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // SECTION D — DECISION PRESSURE & RISK (5 Questions)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "q15",
      section: "D",
      sectionTitle: "Decision Pressure & Risk",
      sectionIcon: "AlertTriangle",
      question: "Do you feel pressure to \"do something\" about A.I.?",
      type: "radio",
      options: [
        { value: "not_at_all", label: "Not at all" },
        { value: "mild", label: "Mild" },
        { value: "moderate", label: "Moderate" },
        { value: "urgent", label: "Urgent" }
      ]
    },
    {
      id: "q16",
      section: "D",
      sectionTitle: "Decision Pressure & Risk",
      question: "Where is that pressure coming from?",
      subtitle: "Select all that apply",
      type: "multiselect",
      maxSelections: 6,
      options: [
        { value: "market_competition", label: "Market competition" },
        { value: "customers", label: "Customers" },
        { value: "internal_staff", label: "Internal staff" },
        { value: "industry_messaging", label: "Industry messaging" },
        { value: "personal_concern", label: "Personal concern" },
        { value: "no_pressure", label: "No pressure" }
      ]
    },
    {
      id: "q17",
      section: "D",
      sectionTitle: "Decision Pressure & Risk",
      question: "What would the cost of a poor technology decision be?",
      type: "radio",
      options: [
        { value: "minimal", label: "Minimal" },
        { value: "manageable", label: "Manageable" },
        { value: "significant", label: "Significant" },
        { value: "existential", label: "Existential" }
      ]
    },
    {
      id: "q18",
      section: "D",
      sectionTitle: "Decision Pressure & Risk",
      question: "What is your biggest fear regarding inaction?",
      type: "radio",
      options: [
        { value: "losing_efficiency", label: "Losing efficiency" },
        { value: "losing_competitiveness", label: "Losing competitiveness" },
        { value: "rushed_decision_later", label: "Making a rushed decision later" },
        { value: "falling_behind_peers", label: "Falling behind peers" },
        { value: "no_major_fear", label: "No major fear" }
      ]
    },
    {
      id: "q19",
      section: "D",
      sectionTitle: "Decision Pressure & Risk",
      question: "What matters more right now?",
      type: "radio",
      options: [
        { value: "speed", label: "Speed" },
        { value: "certainty", label: "Certainty" },
        { value: "cost_control", label: "Cost control" },
        { value: "risk_reduction", label: "Risk reduction" }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // SECTION E — READINESS & INTENT (3 Questions)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "q20",
      section: "E",
      sectionTitle: "Readiness & Intent",
      sectionIcon: "Rocket",
      question: "How do you typically make major technology decisions?",
      type: "radio",
      options: [
        { value: "internally_experience", label: "Internally, based on experience" },
        { value: "external_advisors", label: "With external advisors" },
        { value: "vendor_input", label: "Based on vendor input" },
        { value: "case_by_case", label: "Case-by-case" }
      ]
    },
    {
      id: "q21",
      section: "E",
      sectionTitle: "Readiness & Intent",
      question: "What outcome would make this evaluation valuable to you?",
      type: "radio",
      options: [
        { value: "clearer_risks", label: "Clearer understanding of risks" },
        { value: "better_questions", label: "Better questions to ask" },
        { value: "validation_thinking", label: "Validation of current thinking" },
        { value: "confidence_forward", label: "Confidence to move forward or wait" }
      ]
    },
    {
      id: "q22",
      section: "E",
      sectionTitle: "Readiness & Intent",
      question: "Optional Context",
      subtitle: "If there is anything else about your business or concerns you feel is important, you may share it here.",
      type: "textarea",
      optional: true,
      maxLength: 500,
      placeholder: "Share any additional context about your business situation, concerns, or goals... (500 characters max)"
    }
  ];
};

// Section configuration for step navigation
const sections = [
  { id: "A", title: "Business Identity & Scale", icon: Building2, questions: 6 },
  { id: "B", title: "Operational Reality", icon: Cog, questions: 5 },
  { id: "C", title: "A.I. Awareness", icon: Brain, questions: 5 },
  { id: "D", title: "Decision Pressure", icon: AlertTriangle, questions: 5 },
  { id: "E", title: "Readiness & Intent", icon: Rocket, questions: 3 }
];

const sectionIcons = {
  Building2,
  Cog,
  Brain,
  AlertTriangle,
  Rocket
};

// Progress status messages for report generation
const progressMessages = [
  "Analyzing business context...",
  "Identifying operational patterns...",
  "Evaluating decision pressures...",
  "Assessing industry landscape...",
  "Generating strategic insights...",
  "Finalizing executive summary..."
];

const QuestionnairePage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Account creation modal state
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  
  // Report generation state
  const [showGeneratingModal, setShowGeneratingModal] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [currentProgressMessage, setCurrentProgressMessage] = useState(0);
  
  // Report ready modal state
  const [showReportReadyModal, setShowReportReadyModal] = useState(false);

  // Build questions based on current answers
  const questions = buildQuestions(answers);
  
  // Filter out skipped questions
  const activeQuestions = questions.filter(q => !q.skipIf);

  useEffect(() => {
    const fetchLead = async () => {
      try {
        const response = await axios.get(`${API}/public/lead/${leadId}`);
        setLead(response.data);
        
        // If questionnaire already completed, check account status
        if (response.data.questionnaire_completed) {
          // Check if user account exists
          if (response.data.has_user_account) {
            navigate(`/login?redirect=/portal&email=${encodeURIComponent(response.data.email)}`);
          } else {
            // Show account creation modal
            setShowAccountModal(true);
          }
        }
      } catch (error) {
        toast.error("Unable to load your information");
        navigate("/get-started");
      } finally {
        setIsLoading(false);
      }
    };
    fetchLead();
  }, [leadId, navigate]);

  // Progress animation for report generation
  useEffect(() => {
    let progressInterval;
    let messageInterval;
    
    if (showGeneratingModal) {
      progressInterval = setInterval(() => {
        setGenerationProgress(prev => {
          if (prev >= 95) return prev;
          return prev + Math.random() * 3;
        });
      }, 500);
      
      messageInterval = setInterval(() => {
        setCurrentProgressMessage(prev => (prev + 1) % progressMessages.length);
      }, 3000);
    }
    
    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
    };
  }, [showGeneratingModal]);

  const currentQuestion = activeQuestions[currentStep];
  const totalQuestions = activeQuestions.length;
  const progress = ((currentStep + 1) / totalQuestions) * 100;

  // Get current section info
  const currentSection = currentQuestion ? sections.find(s => s.id === currentQuestion.section) : null;
  const SectionIcon = currentQuestion?.sectionIcon ? sectionIcons[currentQuestion.sectionIcon] : null;

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [currentQuestion.id]: value };
    
    // Clear dependent fields when parent changes
    if (currentQuestion.id === "q1") {
      delete newAnswers.q1_sub;
      delete newAnswers.q1_detail;
    } else if (currentQuestion.id === "q1_sub") {
      delete newAnswers.q1_detail;
    }
    
    setAnswers(newAnswers);
  };

  const handleMultiSelect = (value, checked) => {
    const current = answers[currentQuestion.id] || [];
    let updated;
    
    if (value === "no_pressure" && checked) {
      updated = ["no_pressure"];
    } else if (checked) {
      const withoutNoPress = current.filter(v => v !== "no_pressure");
      if (withoutNoPress.length >= (currentQuestion.maxSelections || 3)) {
        toast.error(`You can select up to ${currentQuestion.maxSelections || 3} options`);
        return;
      }
      updated = [...withoutNoPress, value];
    } else {
      updated = current.filter(v => v !== value);
    }
    
    setAnswers({ ...answers, [currentQuestion.id]: updated });
  };

  const isCurrentAnswered = () => {
    if (!currentQuestion) return false;
    if (currentQuestion.optional) return true;
    
    const answer = answers[currentQuestion.id];
    if (currentQuestion.type === "multiselect") {
      return answer && answer.length > 0;
    }
    if (currentQuestion.type === "textarea") {
      return currentQuestion.optional || (answer && answer.trim().length > 0);
    }
    return !!answer;
  };

  const handleNext = () => {
    if (!isCurrentAnswered()) {
      toast.error("Please answer this question to continue");
      return;
    }
    if (currentStep < totalQuestions - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleQuestionnaireSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/public/questionnaire/${leadId}`, {
        responses: answers,
        version: "v1.1"
      });
      
      // Show account creation modal
      setShowAccountModal(true);
    } catch (error) {
      toast.error("Failed to submit questionnaire. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAccount = async () => {
    // Validate
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (!acceptTerms) {
      toast.error("Please accept the terms to continue");
      return;
    }
    
    setIsCreatingAccount(true);
    
    try {
      // Create user account
      await axios.post(`${API}/public/create-account`, {
        lead_id: leadId,
        password: password
      });
      
      setShowAccountModal(false);
      setShowGeneratingModal(true);
      setGenerationProgress(0);
      
      // Trigger report generation
      try {
        await axios.post(`${API}/public/generate-report/${leadId}`);
        setGenerationProgress(100);
        
        // Show report ready modal
        setTimeout(() => {
          setShowGeneratingModal(false);
          setShowReportReadyModal(true);
        }, 1000);
      } catch (genError) {
        toast.error("Report generation failed. Please try again later.");
        setShowGeneratingModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create account");
      setIsCreatingAccount(false);
    }
  };

  const handleLoginNow = () => {
    navigate(`/login?redirect=/portal&email=${encodeURIComponent(lead?.email)}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <section className="py-16 md:py-20 bg-gradient-to-b from-navy-950 to-navy-900">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {/* Header */}
          <div className="text-center mb-6">
            <p className="text-primary font-medium mb-2 tracking-wide uppercase text-xs sm:text-sm">
              A.I. Business Evaluation
            </p>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-white mb-2 tracking-tight">
              Help Us Understand Your Business
            </h1>
            <p className="text-slate-400 text-sm">
              ~12–15 minutes • Your responses remain confidential
            </p>
          </div>

          {/* Section Progress Indicators */}
          <div className="mb-6">
            <div className="flex justify-between text-xs text-slate-400 mb-2">
              <span>Question {currentStep + 1} of {totalQuestions}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <div className="h-2 bg-navy-800 rounded-full overflow-hidden mb-4">
              <div
                className="h-full bg-primary transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            
            {/* Section tabs */}
            <div className="flex gap-1 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
              {sections.map((section, idx) => {
                const isCurrent = currentQuestion?.section === section.id;
                const isPast = sections.findIndex(s => s.id === currentQuestion?.section) > idx;
                
                return (
                  <div
                    key={section.id}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-all ${
                      isCurrent
                        ? "bg-primary text-white font-medium"
                        : isPast
                        ? "bg-primary/20 text-primary"
                        : "bg-navy-800 text-slate-500"
                    }`}
                  >
                    <section.icon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{section.title}</span>
                    <span className="sm:hidden">{section.id}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Question Card */}
          {currentQuestion && (
            <div className="glass p-5 sm:p-8 rounded-2xl" data-testid="questionnaire-card">
              {/* Section header */}
              {SectionIcon && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <SectionIcon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-primary text-sm font-medium">
                      Section {currentQuestion.section}: {currentQuestion.sectionTitle}
                    </p>
                  </div>
                </div>
              )}
              
              <h2 className="font-heading text-lg sm:text-xl font-semibold text-white mb-1">
                {currentQuestion.question}
                {currentQuestion.optional && (
                  <span className="text-slate-500 text-sm font-normal ml-2">(Optional)</span>
                )}
              </h2>
              {currentQuestion.subtitle && (
                <p className="text-slate-400 text-sm mb-4">{currentQuestion.subtitle}</p>
              )}

              <div className="mt-5">
                {/* Dropdown Questions */}
                {currentQuestion.type === "dropdown" && (
                  <Select
                    value={answers[currentQuestion.id] || ""}
                    onValueChange={handleAnswer}
                  >
                    <SelectTrigger 
                      className="bg-navy-900/50 border-slate-700 text-white h-12"
                      data-testid={`select-${currentQuestion.id}`}
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent className="bg-navy-900 border-slate-700 max-h-72 z-50">
                      {currentQuestion.options.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="text-white hover:bg-primary/20 focus:bg-primary/20"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Radio Questions */}
                {currentQuestion.type === "radio" && (
                  <RadioGroup
                    value={answers[currentQuestion.id] || ""}
                    onValueChange={handleAnswer}
                    className="space-y-2"
                  >
                    {currentQuestion.options.map((option) => (
                      <div
                        key={option.value}
                        className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                          answers[currentQuestion.id] === option.value
                            ? "border-primary bg-primary/10"
                            : "border-slate-700 hover:border-slate-600"
                        }`}
                        onClick={() => handleAnswer(option.value)}
                        data-testid={`radio-${currentQuestion.id}-${option.value}`}
                      >
                        <RadioGroupItem
                          value={option.value}
                          id={`${currentQuestion.id}-${option.value}`}
                          className="border-slate-600 text-primary"
                        />
                        <Label
                          htmlFor={`${currentQuestion.id}-${option.value}`}
                          className="text-slate-200 cursor-pointer flex-1 text-sm"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}

                {/* Multi-Select Questions */}
                {currentQuestion.type === "multiselect" && (
                  <div className="space-y-2">
                    {currentQuestion.options.map((option) => {
                      const isChecked = (answers[currentQuestion.id] || []).includes(option.value);
                      const isDisabled = option.value !== "no_pressure" && 
                        (answers[currentQuestion.id] || []).includes("no_pressure");
                      
                      return (
                        <div
                          key={option.value}
                          className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                            isChecked
                              ? "border-primary bg-primary/10"
                              : isDisabled
                              ? "border-slate-800 opacity-50"
                              : "border-slate-700 hover:border-slate-600"
                          }`}
                          onClick={() => !isDisabled && handleMultiSelect(option.value, !isChecked)}
                          data-testid={`checkbox-${currentQuestion.id}-${option.value}`}
                        >
                          <Checkbox
                            id={`${currentQuestion.id}-${option.value}`}
                            checked={isChecked}
                            disabled={isDisabled}
                            onCheckedChange={(checked) => handleMultiSelect(option.value, checked)}
                            className="border-slate-600 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                          />
                          <Label
                            htmlFor={`${currentQuestion.id}-${option.value}`}
                            className={`cursor-pointer flex-1 text-sm ${isDisabled ? "text-slate-500" : "text-slate-200"}`}
                          >
                            {option.label}
                          </Label>
                        </div>
                      );
                    })}
                    <p className="text-slate-500 text-xs mt-2">
                      Selected: {(answers[currentQuestion.id] || []).length} of {currentQuestion.maxSelections}
                    </p>
                  </div>
                )}

                {/* Textarea Questions */}
                {currentQuestion.type === "textarea" && (
                  <div>
                    <Textarea
                      value={answers[currentQuestion.id] || ""}
                      onChange={(e) => {
                        if (e.target.value.length <= (currentQuestion.maxLength || 500)) {
                          handleAnswer(e.target.value);
                        }
                      }}
                      placeholder={currentQuestion.placeholder}
                      rows={5}
                      className="bg-navy-900/50 border-slate-700 text-white placeholder:text-slate-500 resize-none"
                      data-testid={`textarea-${currentQuestion.id}`}
                    />
                    <p className="text-slate-500 text-xs mt-2 text-right">
                      {(answers[currentQuestion.id] || "").length} / {currentQuestion.maxLength || 500}
                    </p>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-6 pt-4 border-t border-slate-800">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  className="border-slate-700 text-slate-300 hover:text-white"
                  data-testid="questionnaire-prev"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>

                {currentStep < totalQuestions - 1 ? (
                  <Button
                    onClick={handleNext}
                    disabled={!isCurrentAnswered()}
                    className="bg-primary hover:bg-primary/90 text-white"
                    data-testid="questionnaire-next"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleQuestionnaireSubmit}
                    disabled={isSubmitting}
                    className="bg-primary hover:bg-primary/90 text-white"
                    data-testid="questionnaire-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Complete Questionnaire
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Reassurance footer */}
          <div className="text-center mt-6">
            <p className="text-slate-500 text-xs">
              There are no right or wrong answers. Your information is stored securely and will never be shared.
            </p>
          </div>
        </div>
      </section>

      {/* Account Creation Modal */}
      <Dialog open={showAccountModal} onOpenChange={setShowAccountModal}>
        <DialogContent className="bg-navy-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              Secure Your Evaluation Report
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              You've provided the information needed to prepare your AI Business Evaluation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-slate-300 text-sm">
              Before we generate your report, we need to secure your account. This ensures your evaluation remains private, accessible, and available to you at any time.
            </p>
            
            <div className="space-y-3">
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-wider">Email Address</Label>
                <Input 
                  value={lead?.email || ""} 
                  disabled 
                  className="bg-navy-950 border-slate-700 text-slate-400 mt-1"
                />
                <p className="text-slate-500 text-xs mt-1">Your email address will serve as your User ID.</p>
              </div>
              
              <div>
                <Label className="text-slate-300 text-sm">Create Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    className="bg-navy-950 border-slate-700 text-white pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300 text-sm">Confirm Password</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  className="bg-navy-950 border-slate-700 text-white mt-1"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <div className="flex items-start space-x-3 pt-2">
                <Checkbox
                  id="accept-terms"
                  checked={acceptTerms}
                  onCheckedChange={setAcceptTerms}
                  className="border-slate-600 data-[state=checked]:bg-primary mt-0.5"
                />
                <Label htmlFor="accept-terms" className="text-slate-300 text-sm cursor-pointer">
                  I understand that my evaluation report will be generated once and stored securely in my account.
                </Label>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleCreateAccount}
              disabled={isCreatingAccount || !password || !confirmPassword || !acceptTerms}
              className="bg-primary hover:bg-primary/90 text-white w-full"
              data-testid="create-account-btn"
            >
              {isCreatingAccount ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Create Account & Generate Report
                </>
              )}
            </Button>
            <p className="text-slate-500 text-xs text-center">
              This step protects your information and prevents unauthorized access.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Generating Modal */}
      <Dialog open={showGeneratingModal} onOpenChange={() => {}}>
        <DialogContent className="bg-navy-900 border-slate-700 max-w-md" hideClose>
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
            
            <h2 className="font-heading text-xl font-semibold text-white mb-2">
              Preparing Your AI Business Evaluation
            </h2>
            
            <p className="text-slate-400 text-sm mb-6">
              We are generating a customized evaluation based on the information you provided.
              This process typically takes less than one minute and is completed only once for your business profile.
            </p>
            
            {/* Progress bar */}
            <div className="w-full bg-navy-800 rounded-full h-2 mb-4">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-500"
                style={{ width: `${generationProgress}%` }}
              />
            </div>
            
            {/* Status message */}
            <p className="text-primary text-sm font-medium">
              {progressMessages[currentProgressMessage]}
            </p>
            
            <p className="text-slate-500 text-xs mt-6">
              Once complete, your report will be available in your secure portal.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Ready Modal */}
      <Dialog open={showReportReadyModal} onOpenChange={setShowReportReadyModal}>
        <DialogContent className="bg-navy-900 border-slate-700 max-w-md">
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            
            <h2 className="font-heading text-xl font-semibold text-white mb-2">
              Your Evaluation Is Ready
            </h2>
            
            <p className="text-slate-400 text-sm mb-6">
              Your AI Business Evaluation has been completed and securely stored.
              Please sign in to access your report and your AI Means Business portal.
            </p>
            
            <Button
              onClick={handleLoginNow}
              className="bg-primary hover:bg-primary/90 text-white w-full"
              data-testid="login-now-btn"
            >
              Sign In to View My Report
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PublicFooter />
    </div>
  );
};

export default QuestionnairePage;
